(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports, require('@by-association-only/theme-utils')) :
  typeof define === 'function' && define.amd ? define(['exports', '@by-association-only/theme-utils'], factory) :
  (global = global || self, factory(global['dist/theme-module'] = {}, global.themeUtils));
}(this, (function (exports, themeUtils) { 'use strict';

  function defineTargetEls(constructor) {
    const prototype = constructor.prototype;
    const targetNames = getTargetNamesForConstructor(constructor);
    targetNames.forEach((name) => defineLinkedProperties(prototype, {
      [`${name}El`]: {
        get() {
          const target = this.targets.find(name);
          if (target) {
            return target;
          }
          console.info(`Missing target element "${this.identifier}.${name}`);
        }
      },
      [`${name}Els`]: {
        get() {
          return this.targets.findAll(name);
        }
      },
      [`has${capitalize(name)}El`]: {
        get() {
          return this.targets.has(name);
        }
      }
    }));
    buildSelectors(prototype, targetNames);
  }
  function defineExternalTargetEls(constructor) {
    const prototype = constructor.prototype;
    const targetNames = getExternalTargetNamesForConstructor(constructor);
    targetNames.forEach((name) => {
      const getSelector = (instance) => `[${instance.context.schema.externalElAttribute}*="${instance.identifier}.${name}"]`;
      defineLinkedProperties(prototype, {
        [`${name}ExternalEl`]: {
          get() {
            const el = themeUtils.getElement(getSelector(this));
            if (el) {
              return el;
            }
            console.info(`Missing target element "${this.identifier}.${name}`);
          }
        },
        [`${name}ExternalEls`]: {
          get() {
            return themeUtils.getElements(getSelector(this));
          }
        },
        [`has${capitalize(name)}ExternalEl`]: {
          get() {
            return !!themeUtils.getElement(getSelector(this));
          }
        }
      });
    });
    buildExternalSelectors(prototype, targetNames);
  }
  function getTargetNamesForConstructor(constructor) {
    const ancestors = getAncestorsForConstructor(constructor);
    return Array.from(ancestors.reduce((targetNames, constructor2) => {
      getOwnTargetNamesForConstructor(constructor2).forEach((name) => targetNames.add(name));
      return targetNames;
    }, new Set()));
  }
  function getExternalTargetNamesForConstructor(constructor) {
    const ancestors = getAncestorsForConstructor(constructor);
    return Array.from(ancestors.reduce((targetNames, constructor2) => {
      getOwnTargetNamesForConstructor(constructor2, "externalTargets").forEach((name) => targetNames.add(name));
      return targetNames;
    }, new Set()));
  }
  function getAncestorsForConstructor(constructor) {
    const ancestors = [];
    while (constructor) {
      ancestors.push(constructor);
      constructor = Object.getPrototypeOf(constructor);
    }
    return ancestors;
  }
  function getOwnTargetNamesForConstructor(constructor, property = "targets") {
    const definition = constructor[property];
    return Array.isArray(definition) ? definition : [];
  }
  function defineLinkedProperties(object, properties) {
    Object.keys(properties).forEach((name) => {
      if (!(name in object)) {
        const descriptor = properties[name];
        Object.defineProperty(object, name, descriptor);
      }
    });
  }
  function capitalize(name) {
    return name.charAt(0).toUpperCase() + name.slice(1);
  }
  function buildSelectors(object, targets) {
    if (!("selectors" in object)) {
      Object.defineProperty(object, "selectors", {
        get() {
          return targets.reduce((selectors, target) => {
            selectors[target] = `[${this.context.schema.elAttribute}*="${this.context.identifier}.${target}"]`;
            return selectors;
          }, {});
        }
      });
    }
  }
  function buildExternalSelectors(object, targets) {
    if (!("externalSelectors" in object)) {
      Object.defineProperty(object, "externalSelectors", {
        get() {
          return targets.reduce((selectors, target) => {
            selectors[target] = `[${this.context.schema.externalElAttribute}*="${this.context.identifier}.${target}"]`;
            return selectors;
          }, {});
        }
      });
    }
  }

  function blessState(constructor, module) {
    const initialStateProperties = getInitialStatePropertiesForConstructor(constructor);
    initialStateProperties.forEach((property) => addInitialState(module, property));
  }
  function getInitialStatePropertiesForConstructor(constructor) {
    const ancestors = getAncestorsForConstructor(constructor);
    return Array.from(ancestors.reduce((initialStateProperties, constructor2) => {
      getOwnInitialStatePropertiesForConstructor(constructor2).forEach((property) => initialStateProperties.add(property));
      return initialStateProperties;
    }, new Set()));
  }
  function getOwnInitialStatePropertiesForConstructor(constructor) {
    const definition = constructor["initialState"];
    return Array.isArray(definition) ? definition : [];
  }
  function addInitialState(module, property) {
    if (!module.data.has(property.name)) {
      console.info(`Can't find ${module.data._getFormattedKey(property.name)} on ${module.identifier}'s element so can't set the initial state for it`);
    }
    switch (property.type) {
      case Number: {
        module.state.addKey(property.name, parseInt(module.data.get(property.name), 10));
        break;
      }
      case Boolean: {
        module.state.addKey(property.name, module.data.get(property.name) === "true");
        break;
      }
      default: {
        module.state.addKey(property.name, module.data.get(property.name));
      }
    }
  }

  class DataMap {
    constructor(context) {
      this.context = context;
    }
    get element() {
      return this.context.element;
    }
    get identifier() {
      return this.context.identifier;
    }
    get state() {
      return this.context.state;
    }
    get(key) {
      return this.element.getAttribute(this._getFormattedKey(key));
    }
    set(key, value) {
      this.element.setAttribute(this._getFormattedKey(key), value);
      if (!this.state.hasKey(key)) {
        this.state.addKey(key, value);
      }
      this.state[key] = value;
      return this.get(key);
    }
    has(key) {
      return this.element.hasAttribute(this._getFormattedKey(key));
    }
    delete(key) {
      if (this.has(key)) {
        this.element.removeAttribute(this._getFormattedKey(key));
        return true;
      }
      return false;
    }
    _getFormattedKey(key) {
      return `data-${this.identifier}-${dasherize(key)}`;
    }
  }
  function dasherize(value) {
    return value.replace(/([A-Z])/g, (_, char) => `-${char.toLowerCase()}`);
  }

  class TargetSet {
    constructor(context) {
      this.context = context;
    }
    get element() {
      return this.context.element;
    }
    get identifier() {
      return this.context.identifier;
    }
    get schema() {
      return this.context.schema;
    }
    has(targetName) {
      return this.find(targetName) != null;
    }
    find(...targetNames) {
      const selector = this._getSelectorForTargetNames(targetNames);
      return this.context.getElement(selector);
    }
    findAll(...targetNames) {
      const selector = this._getSelectorForTargetNames(targetNames);
      return this.context.getElements(selector);
    }
    _getSelectorForTargetNames(targetNames) {
      return targetNames.map((targetName) => this._getSelectorForTargetName(targetName)).join(", ");
    }
    _getSelectorForTargetName(targetName) {
      const targetDescriptor = `${this.identifier}.${targetName}`;
      return attributeValueContainsToken(this.schema.elAttribute, targetDescriptor);
    }
  }
  function attributeValueContainsToken(attributeName, token) {
    return `[${attributeName}~="${token}"]`;
  }

  class Context {
    constructor(element, application) {
      this.element = element;
      this.application = application;
      this.state = new themeUtils.ReactiveState({});
      this.targets = new TargetSet(this);
      this.data = new DataMap(this);
    }
    get identifier() {
      return this.element.getAttribute(this.schema.moduleAttribute);
    }
    get schema() {
      return this.application.schema;
    }
    getElement(selector) {
      return themeUtils.getElement(selector, {context: this.element});
    }
    getElements(selector) {
      return themeUtils.getElements(selector, {context: this.element});
    }
  }

  function bindInstanceMethods(constructor, module) {
    const instanceMethodNames = getMethodNamesForConstructor(constructor);
    instanceMethodNames.forEach((name) => {
      module[name] = module[name].bind(module);
    });
  }
  function getMethodNamesForConstructor(constructor) {
    const ancestors = getAncestorsForConstructor(constructor);
    return Array.from(ancestors.reduce((targetNames, constructor2) => {
      getOwnTargetNamesForConstructor$1(constructor2).forEach((name) => targetNames.add(name));
      return targetNames;
    }, new Set()));
  }
  function getOwnTargetNamesForConstructor$1(constructor) {
    const definition = constructor["methods"];
    return Array.isArray(definition) ? definition : [];
  }

  class Module {
    static build(el, application) {
      const context = new Context(el, application);
      const module = new this(el, context);
      defineTargetEls(this);
      defineExternalTargetEls(this);
      bindInstanceMethods(this, module);
      blessState(this, module);
      module.initialize();
      module.setupListeners();
      module.ready();
      return module;
    }
    constructor(el, context) {
      this.el = el;
      this.context = context;
      this.listeners = new themeUtils.Listeners();
    }
    get element() {
      return this.context.element;
    }
    get identifier() {
      return this.context.identifier;
    }
    get data() {
      return this.context.data;
    }
    get state() {
      return this.context.state;
    }
    get targets() {
      return this.context.targets;
    }
    initialize() {
    }
    setupListeners() {
    }
    ready() {
    }
    destroy() {
      this.listeners.removeAll();
    }
  }

  exports.Module = Module;

  Object.defineProperty(exports, '__esModule', { value: true });

})));
//# sourceMappingURL=theme-module.umd.js.map
